import { toApiError } from '@/shared/api';

const NO_ANSWER = new Set([0, 502, 503, 504]);

export type SecurityAction = 'begin' | 'confirm' | 'regenerate' | 'disable';

const OPEN: Record<SecurityAction, string> = {
  begin: 'Keine Antwort vom Server – starte die Einrichtung noch einmal.',
  confirm:
    'Keine Antwort vom Server – offen, ob Zwei-Faktor jetzt aktiv ist. Der Stand lädt neu; ist sie aktiv, erzeuge gleich neue Wiederherstellungscodes, denn die alten hast du nicht gesehen.',
  regenerate:
    'Keine Antwort vom Server – offen, ob neue Codes entstanden sind. Dann gelten deine alten nicht mehr: erzeuge sie jetzt noch einmal.',
  disable: 'Keine Antwort vom Server – ob es geklappt hat, zeigt der neu geladene Stand.',
};

/**
 * Deutscher Satz zu einer gescheiterten Änderung der Kontosicherheit, je Schritt. Der Server meldet
 * bei neuen Codes und beim Ausschalten falsches Passwort, falschen Code und Sperre gleich
 * (`Auth.ReauthenticationFailed`), beim Bestätigen der Einrichtung falschen und abgelaufenen Code
 * gleich – der Satz nennt deshalb beides. `unknown`: ohne Antwort ist offen, ob es gewirkt hat.
 */
export async function securityFailure(
  error: unknown,
  action: SecurityAction,
): Promise<{ text: string; unknown: boolean }> {
  const problem = await toApiError(error);
  const known = (text: string) => ({ text, unknown: false });
  if (NO_ANSWER.has(problem.status)) return { text: OPEN[action], unknown: true };
  if (problem.status === 429)
    return known('Zu viele Versuche. Warte einen Moment und versuch es dann noch einmal.');
  const fields = Object.keys(problem.fieldErrors ?? {}).map((key) => key.toLowerCase());
  if (problem.status === 400 && fields.includes('code'))
    return known('Der Code hat sechs Ziffern aus der Authenticator-App.');
  switch (problem.title) {
    case 'Auth.ReauthenticationFailed':
      return known(
        action === 'begin'
          ? 'Das Passwort stimmt nicht. Nach mehreren Fehlversuchen ist die Prüfung 15 Minuten gesperrt.'
          : 'Passwort oder Code stimmt nicht – oder die Prüfung ist nach mehreren Fehlversuchen 15 Minuten gesperrt. Nimm den aktuellen Code aus der App.',
      );
    case 'Auth.InvalidTwoFactorCode':
      return known(
        'Code nicht angenommen: falsch, oder die Einrichtung ist abgelaufen (nach 5 Minuten oder 5 Fehlversuchen). Klappt es wieder nicht, brich ab und starte neu.',
      );
    case 'Auth.TwoFactorAlreadyEnabled':
      return known('Die Zwei-Faktor-Anmeldung ist schon aktiv.');
    default:
      return known(`Das hat nicht geklappt (Status ${problem.status}).`);
  }
}
